// ════════════════════════════════════════════════════════════════
// MASTER SHELL — Universal Dashboard Layout (Scalable Factory Core)
// ════════════════════════════════════════════════════════════════
//
// This component renders the ENTIRE app shell based on AppConfig.
// To deploy a new niche: create a new AppConfig, import it in
// main.tsx, and the shell adapts automatically.
//
// Layout structure (identical across ALL niche deployments):
// ┌──────────────────────────────────────┐
// │            HEADER                     │  ← App name, lock button
// ├──────────┬───────────────────────────┤
// │          │                            │
// │  NAV     │    CONTENT FRAME           │  ← Canvas-compatible
// │  RAIL    │    (renders active widget) │
// │          │                            │
// │          ├───────────────────────────┤
// │          │   MONETIZATION SLOT        │  ← Fixed position (ads/paywall)
// └──────────┴───────────────────────────┘
// On mobile: NavRail becomes a bottom bar.
// ════════════════════════════════════════════════════════════════

import { useState, type ReactNode } from 'react';
import type { AppConfig, WidgetDefinition } from '../config/app.config';
import { useVaultStore } from '../stores/vaultStore';
import { useLicenseStore } from '../stores/licenseStore';
import { ContentFrame } from './ContentFrame';
import { Paywall } from '../components/Paywall';

interface MasterShellProps {
  config: AppConfig;
  children: (activeWidget: WidgetDefinition) => ReactNode;
}

export function MasterShell({ config, children }: MasterShellProps) {
  const [activeWidgetId, setActiveWidgetId] = useState(config.widgets[0]?.id || '');
  const [showPaywall, setShowPaywall] = useState(false);
  const isUnlocked = useVaultStore((s) => s.isUnlocked);
  const lockVault = useVaultStore((s) => s.lockVault);
  const isPro = useLicenseStore((s) => s.isPro);

  const activeWidget = config.widgets.find((w) => w.id === activeWidgetId) || config.widgets[0];

  return (
    <div className="flex flex-col h-screen h-dvh bg-vault-950 text-vault-100">
      {/* ── HEADER ── */}
      <header
        className="flex items-center justify-between px-4 py-3 border-b border-vault-800 bg-vault-900/80 backdrop-blur-sm shrink-0"
        style={{ borderBottomColor: config.app.themeColor + '33' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-lg text-white shrink-0"
            style={{ backgroundColor: config.app.accentColor }}
          >
            {config.app.shortName.charAt(0)}
          </div>
          <div>
            <h1 className="text-sm font-semibold text-vault-100 leading-tight">
              {config.app.name}
            </h1>
            <p className="text-xs text-vault-500 leading-tight">{config.app.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Pro badge */}
          {isPro && (
            <span className="vault-badge bg-accent-500/20 text-accent-400 border border-accent-500/30">
              PRO
            </span>
          )}
          {/* Lock button */}
          {isUnlocked && (
            <button
              onClick={lockVault}
              className="vault-button-ghost !p-2"
              title="Lock vault"
              aria-label="Lock vault"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </button>
          )}
        </div>
      </header>

      {/* ── MAIN BODY: Nav + Content ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar nav */}
        <nav className="hidden md:flex flex-col gap-1 p-3 border-r border-vault-800 w-20 shrink-0">
          {config.widgets.map((widget) => (
            <NavButton
              key={widget.id}
              widget={widget}
              isActive={widget.id === activeWidgetId}
              onClick={() => setActiveWidgetId(widget.id)}
            />
          ))}
        </nav>

        {/* Content area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Content frame (canvas-compatible) */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <ContentFrame // @ts-ignore
        config={config} widget={activeWidget}>
              {children(activeWidget)}
            </ContentFrame>
          </div>

          {/* ── MONETIZATION SLOT (fixed position across all niches) ── */}
          <div className="monetization-slot shrink-0">
            {config.monetization.adSlotEnabled && !isPro ? (
              <AdSlotPlaceholder
                placeholder={config.monetization.adSlotPlaceholder}
                onUpgradeClick={() => setShowPaywall(true)}
              />
            ) : (
              <div className="text-xs text-vault-600">
                {isPro ? 'Pro activated — ad-free' : 'Ads disabled'}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── MOBILE BOTTOM NAV ── */}
      <nav className="md:hidden flex items-center justify-around border-t border-vault-800 bg-vault-900/80 backdrop-blur-sm shrink-0">
        {config.widgets.map((widget) => (
          <button
            key={widget.id}
            onClick={() => setActiveWidgetId(widget.id)}
            className={`vault-nav-item flex-1 ${
              widget.id === activeWidgetId ? 'vault-nav-item-active' : 'vault-nav-item-inactive'
            }`}
          >
            <NavIcon icon={widget.icon} />
            <span className="text-[10px] font-medium">{widget.label}</span>
          </button>
        ))}
      </nav>

      {/* ── PAYWALL MODAL ── */}
      {showPaywall && (
        <Paywall
          // @ts-ignore
        config={config}
          onClose={() => setShowPaywall(false)}
        />
      )}
    </div>
  );
}

// ── Nav Button (desktop sidebar) ──
function NavButton({
  widget,
  isActive,
  onClick,
}: {
  widget: WidgetDefinition;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`vault-nav-item ${isActive ? 'vault-nav-item-active' : 'vault-nav-item-inactive'}`}
      title={widget.label}
    >
      <NavIcon icon={widget.icon} />
      <span className="text-[10px] font-medium text-center leading-tight">{widget.label}</span>
    </button>
  );
}

// ── Nav Icon renderer (maps string names to SVG paths) ──
function NavIcon({ icon }: { icon: string }) {
  const icons: Record<string, ReactNode> = {
    creditcard: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>,
    'chart.pie': <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>,
    clock: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    gearshape: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>,
    bitcoinsign: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
    qrcode: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><line x1="14" y1="14" x2="14" y2="21" /><line x1="18" y1="14" x2="21" y2="14" /><line x1="21" y1="18" x2="21" y2="21" /></svg>,
  };
  return icons[icon] || icons.gearshape;
}

// ── Ad Slot Placeholder (same position across all niches) ──
function AdSlotPlaceholder({
  placeholder,
  onUpgradeClick,
}: {
  placeholder: string;
  onUpgradeClick: () => void;
}) {
  return (
    <div className="w-full flex items-center justify-between gap-3">
      {/* ── AD CONTAINER ──
          This is where you insert Google AdMob (via web adapter),
          Google AdSense, or any ad network's banner code.
          The slot stays in this exact position across ALL niche
          deployments. */}
      <div
        className="flex-1 h-[50px] rounded-lg border border-dashed border-vault-700 flex items-center justify-center text-xs text-vault-500"
        data-ad-slot="banner"
        data-ad-client="ca-app-pub-XXXXXXXXXXXXXXXX"
      >
        {placeholder}
      </div>
      <button
        onClick={onUpgradeClick}
        className="vault-button-secondary !py-1.5 !px-3 text-xs whitespace-nowrap"
      >
        Remove Ads
      </button>
    </div>
  );
}


